import React from 'react';
import { Link } from 'react-router-dom';
import { Laptop, Monitor, Box, Cpu, Keyboard, ShieldCheck, Wrench, BadgeCheck, Truck, MessageCircle, ArrowRight, ExternalLink } from 'lucide-react';
import { CallbackButton } from './CallbackModal';

export const DATEN_STORE_URL = 'https://loja.daten.com.br';
const WA = (msg) => `https://wa.me/5516991378312?text=${encodeURIComponent(msg)}`;

export const DATEN_LINES = [
  { icon: Laptop,  name: 'Notebooks Daten',   desc: 'Linha corporativa e educacional, Windows 11 Pro, garantia nacional.', from: 'Ideal para empresas, escolas e órgãos públicos', url: `${DATEN_STORE_URL}/collections/notebooks` },
  { icon: Monitor, name: 'Desktops Daten',    desc: 'Desktops slim e PDV com Intel Core / AMD Ryzen PRO, SSD e baixo consumo.', from: 'A partir de R$ 1.911 na loja Daten', url: `${DATEN_STORE_URL}/collections/desktops` },
  { icon: Box,     name: 'Mini PCs Daten',    desc: 'Compactos para PDV, recepção, totens e escritórios: Celeron, i3, i5 e Ryzen.', from: 'A partir de R$ 1.606 na loja Daten', url: `${DATEN_STORE_URL}/collections/mini-pcs` },
  { icon: Cpu,     name: 'All in One Daten',  desc: 'Tudo em um, design limpo e menos cabos para recepções e consultórios.', from: 'Configurações sob consulta', url: `${DATEN_STORE_URL}/collections/all-in-ones` },
  { icon: Keyboard,name: 'Acessórios',        desc: 'Monitores, teclados, mouses e headsets homologados pela Daten.', from: 'Kits para empresas', url: `${DATEN_STORE_URL}/collections/acessorios` },
];

const WHY = [
  { icon: BadgeCheck, t: 'Revenda e afiliado oficial Daten', d: 'Compre com a segurança de um parceiro autorizado da fabricante brasileira com 25 anos de mercado.' },
  { icon: Wrench,     t: 'Assistência técnica autorizada',  d: 'Garantia e pós-venda em Ribeirão Preto: configuramos, instalamos e damos suporte ao seu equipamento.' },
  { icon: ShieldCheck,t: 'Equipamentos homologados',        d: 'Linha empresarial com Windows 11 Pro, TPM 2.0 e compatibilidade para licitações e órgãos públicos.' },
  { icon: Truck,      t: 'Entrega em todo o Brasil',        d: 'Pedido direto da fábrica com nota fiscal; nós cuidamos da configuração e do suporte.' },
];

const LojaDaten = ({ showLearnMore = false }) => (
  <section id="loja-daten" className="py-20 bg-slate-950 relative overflow-hidden" style={{ scrollMarginTop: '100px' }} data-testid="loja-daten-section">
    <div className="absolute inset-0 pointer-events-none">
      <div className="absolute top-10 -right-20 w-96 h-96 bg-blue-500/5 rounded-full blur-3xl" />
    </div>
    <div className="container mx-auto px-4 relative z-10">
      <div className="max-w-3xl mb-12">
        <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-blue-500/10 border border-blue-500/30 rounded-full text-blue-300 text-sm font-semibold mb-4">
          <BadgeCheck className="w-4 h-4" /> Revenda · Afiliado · Assistência Autorizada
        </div>
        <h2 className="text-3xl sm:text-4xl font-extrabold text-white mb-4">
          Loja Daten com <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-blue-400">suporte técnico autorizado</span> da Fernando Tech
        </h2>
        <p className="text-slate-400 leading-relaxed">
          A Fernando Tech é revenda, afiliada e assistência técnica autorizada <strong className="text-white">Daten Tecnologia</strong> em Ribeirão Preto e região.
          Você compra notebooks, desktops, mini PCs e all in one Daten com preço de fábrica e ganha o que nenhuma loja online oferece:
          uma equipe local que configura, instala, dá garantia e suporte técnico de verdade.
        </p>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-12">
        {DATEN_LINES.map(({ icon: Icon, name, desc, from, url }) => (
          <article key={name} className="group rounded-2xl bg-slate-900/70 border border-slate-800 hover:border-blue-500/50 p-5 transition-all hover:-translate-y-1" data-testid={`daten-line-${name.toLowerCase().replace(/\s+/g, '-')}`}>
            <span className="inline-flex items-center justify-center w-11 h-11 rounded-xl bg-blue-500/10 border border-blue-500/20 mb-3">
              <Icon className="w-5 h-5 text-blue-300" />
            </span>
            <h3 className="text-white font-bold mb-1">{name}</h3>
            <p className="text-slate-400 text-sm leading-relaxed mb-2">{desc}</p>
            <p className="text-cyan-300 text-xs font-semibold mb-4">{from}</p>
            <div className="flex flex-wrap gap-2">
              <a href={WA(`Olá! Tenho interesse em ${name}. Pode me passar preço e condições?`)} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold transition-colors">
                <MessageCircle className="w-3.5 h-3.5" /> Cotar no WhatsApp
              </a>
              <a href={url} target="_blank" rel="noopener noreferrer sponsored" className="inline-flex items-center gap-1 px-3.5 py-2 rounded-full bg-slate-800 hover:bg-slate-700 text-slate-300 text-xs font-semibold transition-colors">
                Ver na loja <ExternalLink className="w-3 h-3" />
              </a>
            </div>
          </article>
        ))}
      </div>

      <div className="grid md:grid-cols-2 gap-4 mb-12">
        {WHY.map(({ icon: Icon, t, d }) => (
          <div key={t} className="flex gap-4 rounded-2xl bg-slate-900/50 border border-slate-800 p-5">
            <Icon className="w-6 h-6 text-cyan-400 shrink-0 mt-0.5" />
            <div>
              <h3 className="text-white font-semibold mb-1">{t}</h3>
              <p className="text-slate-400 text-sm leading-relaxed">{d}</p>
            </div>
          </div>
        ))}
      </div>

      <div className="flex flex-col sm:flex-row flex-wrap items-center gap-3">
        <a href={WA('Olá! Quero comprar um equipamento Daten com suporte da Fernando Tech.')} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 px-8 py-3.5 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-sm shadow-lg shadow-emerald-500/20 transition-all hover:scale-105" data-testid="loja-daten-whatsapp">
          <MessageCircle className="w-4 h-4" /> Comprar Daten pelo WhatsApp
        </a>
        <CallbackButton interesse="Loja Daten (notebooks, desktops, mini PCs)" testId="loja-daten-callback" />
        {showLearnMore && (
          <Link to="/loja-daten" className="inline-flex items-center gap-1.5 text-cyan-400 hover:text-cyan-300 text-sm font-semibold" data-testid="loja-daten-learn-more">
            Saiba mais sobre a Loja Daten <ArrowRight className="w-4 h-4" />
          </Link>
        )}
      </div>
    </div>
  </section>
);

export default LojaDaten;
